import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";

const VISA_CATEGORIES = ["Visa free", "eVisa", "Visa required", "eVisa/VOA", "VOA", "eTA", "DAC"];

async function fetchCountrySuggestions(q) {
  const trimmed = String(q || "").trim();
  if (!trimmed) return [];
  try {
    const res = await api.get("/admin/countries/search", { params: { q: trimmed } });
    const data = res.data?.data;
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

function useCountrySuggestions(query, { minChars = 1 } = {}) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    const q = String(query || "").trim();
    if (q.length < minChars) { setItems([]); return; }
    let cancelled = false;
    const timer = setTimeout(async () => {
      const list = await fetchCountrySuggestions(q);
      if (!cancelled) setItems(list);
    }, 220);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query, minChars]);
  return items;
}

function SuggestDropdown({ items, onPick }) {
  if (!items.length) return null;
  return (
    <ul className="absolute left-0 right-0 top-full mt-1 max-h-48 overflow-auto rounded-xl border bg-white shadow-lg z-50 text-sm">
      {items.map((c) => (
        <li key={c.code}>
          <button
            type="button"
            className="w-full text-left px-3 py-2 hover:bg-gray-100"
            onMouseDown={(e) => { e.preventDefault(); onPick(c); }}
          >
            <span className="font-medium">{c.name}</span>{" "}
            <span className="text-gray-500">({c.code})</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function CountryFields({ form, setForm }) {
  const [focus, setFocus] = useState(null);
  const sugCountry = useCountrySuggestions(form.country);
  const sugCode    = useCountrySuggestions(form.country_code);

  const pick = (c) => setForm((s) => ({ ...s, country: c.name, country_code: String(c.code).toUpperCase() }));
  const blur = () => setTimeout(() => setFocus(null), 180);

  return (
    <>
      <div>
        <label className="text-sm font-semibold">Country <span className="text-red-500">*</span></label>
        <div className="relative mt-2">
          <input
            className="w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            placeholder="e.g. Thailand"
            value={form.country}
            onChange={(e) => setForm((s) => ({ ...s, country: e.target.value }))}
            onFocus={() => setFocus("country")}
            onBlur={blur}
          />
          {focus === "country" && <SuggestDropdown items={sugCountry} onPick={pick} />}
        </div>
      </div>

      <div>
        <label className="text-sm font-semibold">Country Code <span className="text-red-500">*</span></label>
        <div className="relative mt-2">
          <input
            className="w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black uppercase"
            placeholder="e.g. TH"
            maxLength={10}
            value={form.country_code}
            onChange={(e) => setForm((s) => ({ ...s, country_code: e.target.value.toUpperCase() }))}
            onFocus={() => setFocus("code")}
            onBlur={blur}
          />
          {focus === "code" && <SuggestDropdown items={sugCode} onPick={pick} />}
        </div>
      </div>
    </>
  );
}

const DEFAULT_FORM = {
  country: "",
  country_code: "",
  visa_category: "",
  is_active: true,
};

export default function IndianVisaUpsertPage() {
  const navigate = useNavigate();
  const { id }   = useParams();
  const isEdit   = Boolean(id);

  const [form, setForm]     = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/admin/indian-visa-requirements/${id}`)
      .then((res) => {
        const d = res.data?.data;
        if (!d) return;
        setForm({
          country:       d.country       || "",
          country_code:  d.country_code  || "",
          visa_category: d.visa_category || "",
          is_active:     d.is_active !== undefined ? d.is_active : true,
        });
      })
      .catch(() => toast.error("Failed to load record"));
  }, [id, isEdit]);

  const validate = () => {
    if (!form.country.trim())       return "Country is required";
    if (!form.country_code.trim())  return "Country Code is required";
    if (!form.visa_category.trim()) return "Visa Category is required";
    if (!VISA_CATEGORIES.includes(form.visa_category)) return "Select a valid visa category";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    const err = validate();
    if (err) return toast.error(err);

    const payload = {
      country:       form.country.trim(),
      country_code:  form.country_code.trim().toUpperCase(),
      visa_category: form.visa_category,
      is_active:     form.is_active,
    };

    setSaving(true);
    try {
      if (!isEdit) {
        await api.post("/admin/indian-visa-requirements", payload);
        toast.success("Country created");
      } else {
        await api.put(`/admin/indian-visa-requirements/${id}`, payload);
        toast.success("Country updated");
      }
      navigate("/admin/indian-visa-requirements");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-xl">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold">
          {isEdit ? "Edit Indian Visa Requirement" : "Add Indian Visa Requirement"}
        </h1>
        <button
          type="button"
          onClick={() => navigate("/admin/indian-visa-requirements")}
          className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50 self-start"
        >
          Back
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <CountryFields form={form} setForm={setForm} />

        <div>
          <label className="text-sm font-semibold">
            Visa Category <span className="text-red-500">*</span>
          </label>
          <select
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            value={form.visa_category}
            onChange={(e) => setForm((s) => ({ ...s, visa_category: e.target.value }))}
          >
            <option value="">Select a category</option>
            {VISA_CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-3">
          <label className="text-sm font-semibold">Active</label>
          <input
            type="checkbox"
            className="w-4 h-4"
            checked={form.is_active}
            onChange={(e) => setForm((s) => ({ ...s, is_active: e.target.checked }))}
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={() => navigate("/admin/indian-visa-requirements")}
            className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold disabled:opacity-60"
          >
            {saving ? "Saving..." : isEdit ? "Save Changes" : "Create"}
          </button>
        </div>
      </form>
    </div>
  );
}
